import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, Languages } from "lucide-react";
import { useI18n } from "../contexts/I18nContext";
import { SITE, buildWhatsAppLink, NAV_LINKS_STATIC } from "../data/static";

export const Navbar = () => {
  const { t, lang, toggleLang } = useI18n();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const onHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const ctaLink = buildWhatsAppLink(
    lang === "hi"
      ? `नमस्ते ${SITE.name}, मुझे आपकी सेवाओं की पूछताछ करनी है।`
      : `Hi ${SITE.name}, I'd like to enquire about your services.`
  );

  // For nav links: hash-links work only on home; otherwise navigate to /#hash
  const linkFor = (item) => {
    if (item.href.startsWith("/#")) {
      return onHome ? item.href.replace("/", "") : item.href;
    }
    return item.href;
  };

  const isExternalRoute = (href) => href.startsWith("/") && !href.includes("#");

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 inset-x-0 z-40 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-[#FAFAF7]/85 border-b border-slate-200/70 shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          <Link to="/" data-testid="navbar-logo" className="flex items-center gap-2.5 group">
            <span className="w-10 h-10 rounded-full bg-[#D1603D] text-white flex items-center justify-center font-display text-lg font-bold shadow-lg shadow-[#D1603D]/25 group-hover:rotate-[-8deg] transition-transform">
              J
            </span>
            <span className="font-display text-[17px] sm:text-lg lg:text-xl font-semibold text-slate-900 leading-tight">
              {t.nav.brandLine1}
              <span className="block text-[10px] sm:text-xs font-body font-medium tracking-[0.2em] uppercase text-[#D1603D] -mt-0.5">
                {t.nav.brandLine2}
              </span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {NAV_LINKS_STATIC.map((item) => {
              const href = linkFor(item);
              const props = {
                "data-testid": `navlink-${item.key}`,
                className:
                  "px-4 py-2 text-sm font-medium text-slate-700 hover:text-[#D1603D] rounded-full hover:bg-[#FADBD1]/40 transition-colors",
              };
              return isExternalRoute(href) ? (
                <Link key={item.key} {...props} to={href}>{t.nav[item.key]}</Link>
              ) : (
                <a key={item.key} {...props} href={href}>{t.nav[item.key]}</a>
              );
            })}
          </nav>

          <div className="flex items-center gap-2 sm:gap-3">
            <button
              data-testid="lang-toggle"
              onClick={toggleLang}
              aria-label={t.switchAria}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-slate-200 bg-white text-slate-900 text-sm font-semibold hover:border-[#D1603D] hover:text-[#D1603D] transition-colors"
            >
              <Languages className="w-4 h-4" />
              <span data-testid="lang-toggle-label">{t.switchTo}</span>
            </button>
            <a
              href={`tel:+${SITE.whatsappNumber}`}
              data-testid="navbar-call-btn"
              className="hidden sm:inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-700 hover:text-[#D1603D] transition-colors"
            >
              <Phone className="w-4 h-4" />
              <span className="hidden md:inline">{SITE.phoneDisplay}</span>
            </a>
            <a
              href={ctaLink}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="navbar-cta-whatsapp"
              className="hidden sm:inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#0F172A] text-white text-sm font-semibold hover:bg-[#D1603D] transition-all duration-300 active:scale-95"
            >
              {t.nav.bookOnWhatsApp}
            </a>
            <button
              data-testid="navbar-mobile-toggle"
              aria-label="Toggle menu"
              onClick={() => setOpen((s) => !s)}
              className="lg:hidden w-10 h-10 rounded-full bg-white border border-slate-200 flex items-center justify-center text-slate-900 hover:bg-slate-50 transition-colors"
            >
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div data-testid="navbar-mobile-drawer" className="lg:hidden bg-white border-t border-slate-200/70 animate-fade-in-up">
          <nav className="px-4 py-4 flex flex-col gap-1">
            {NAV_LINKS_STATIC.map((item) => {
              const href = linkFor(item);
              const props = {
                onClick: () => setOpen(false),
                className: "px-4 py-3 text-base font-medium text-slate-700 hover:text-[#D1603D] hover:bg-[#FADBD1]/30 rounded-xl transition-colors",
              };
              return isExternalRoute(href) ? (
                <Link key={item.key} {...props} to={href}>{t.nav[item.key]}</Link>
              ) : (
                <a key={item.key} {...props} href={href}>{t.nav[item.key]}</a>
              );
            })}
            <a
              href={ctaLink}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setOpen(false)}
              className="mt-2 inline-flex justify-center items-center gap-2 px-5 py-3 rounded-full bg-[#D1603D] text-white text-sm font-semibold"
            >
              {t.nav.bookOnWhatsApp}
            </a>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Navbar;
